import React, { useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  Rating,
  Chip,
  IconButton,
  Paper,
  Collapse,
  TextField,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CommentIcon from "@mui/icons-material/Comment";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SendIcon from "@mui/icons-material/Send";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import RateComment from "./RateComment";
import { toggleLike } from "../../../services/likeService";
import { useDispatch } from "react-redux";
import { popupTimer } from "../../../consts";

const RateCard = ({
  rate,
  expandedComments,
  comments,
  newComments,
  submitting,
  authorized,
  onToggleComments,
  onCommentChange,
  onSubmitComment,
  onDeleteComment,
  currentUser,
  isSpaceOwner,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [newReplies, setNewReplies] = useState({});
  const [submittingReplies, setSubmittingReplies] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(rate.likes || 0);
  const [isLiking, setIsLiking] = useState(false);

  const handleReplyChange = (commentId, value) => {
    setNewReplies(prev => ({
      ...prev,
      [commentId]: value
    }));
  };

  const handleToggleReplies = async (comment) => {
    const newExpandedState = !expandedReplies[comment.id];
    setExpandedReplies(prev => ({
      ...prev,
      [comment.id]: newExpandedState
    }));

    if (newExpandedState) {
      // Fetch replies for this comment
      await onToggleComments(rate, comment.id);
    }
  };

  // Add effect to reset expandedReplies when comments are collapsed
  useEffect(() => {
    if (!expandedComments[rate.id]) {
      setExpandedReplies({});
    }
  }, [expandedComments[rate.id]]);

  const handleSubmitReply = async (commentId) => {
    if (!newReplies[commentId]?.trim() || !authorized) return;

    setSubmittingReplies(prev => ({ ...prev, [commentId]: true }));
    try {
      await onSubmitComment(rate, commentId, newReplies[commentId]);
      
      // Clear the reply input
      setNewReplies(prev => ({ ...prev, [commentId]: "" }));

      // Automatically expand replies and ensure they're visible
      setExpandedReplies(prev => ({
        ...prev,
        [commentId]: true
      }));
      
      // Fetch fresh replies to show the new one
      await onToggleComments(rate, commentId);
    } catch (error) {
      console.error('Failed to submit reply:', error);
    } finally {
      setSubmittingReplies(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const handleLike = async () => {
    if (!authorized || isLiking) return;
    
    setIsLiking(true);
    try {
      const response = await toggleLike({ rating_id: rate.rating_id });
      setLikeCount(response.like_count || 0);
      setIsLiked(!isLiked);
    } catch (error) {
      if (error.message === "UNAUTHORIZED") {
        dispatch({ type: "SET_UNAUTHORIZED" });
      } else {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: error?.response?.data?.error || "Failed to like. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      }
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: { xs: 1.5, sm: 2 },
        border: "1px solid rgba(0, 0, 0, 0.05)",
        backgroundColor: "#fff",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: { xs: "0 2px 8px rgba(0, 0, 0, 0.05)", sm: "0 4px 20px rgba(0, 0, 0, 0.05)" },
          transform: { xs: "none", sm: "translateY(-2px)" },
        },
      }}
    >
      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        mb: { xs: 1.5, sm: 2 },
        flexWrap: { xs: "wrap", sm: "nowrap" },
        gap: { xs: 1, sm: 0 },
      }}>
        <Avatar
          src={rate.profile_image_url}
          alt={rate.user_name}
          sx={{ 
            width: { xs: 36, sm: 40 }, 
            height: { xs: 36, sm: 40 }, 
            mr: { xs: 1.5, sm: 2 },
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            {rate.user_name}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
          >
            {new Date(rate.create_time).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      {rate.average_stars > 0 && (
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          mb: { xs: 1.5, sm: 2 },
          flexWrap: "wrap",
          gap: { xs: 0.5, sm: 0 },
        }}>
          <Rating
            value={rate.average_stars}
            readOnly
            size={isMobile ? "small" : "small"}
            emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
            sx={{ 
              color: "rgba(255, 204, 72, 1)",
              "& .MuiRating-icon": {
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              },
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              ml: { xs: 0.75, sm: 1 },
              fontWeight: 600,
              color: "text.primary",
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            }}
          >
            {rate.average_stars} / 5
          </Typography>
        </Box>
      )}

      {rate.comment && (
        <Typography
          variant="body1"
          sx={{
            mb: { xs: 1.5, sm: 2 },
            color: "text.secondary",
            lineHeight: { xs: 1.5, sm: 1.6 },
            fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
            wordWrap: "break-word",
          }}
        >
          {rate.comment}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: { xs: 1.5, sm: 2 },
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        {rate.tags && rate.tags.length > 0 && (
          <Box sx={{ 
            display: "flex", 
            gap: { xs: 0.75, sm: 1 }, 
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}>
            {rate.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size={isMobile ? "small" : "small"}
                icon={<LocalOfferIcon sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }} />}
                sx={{
                  backgroundColor: "rgba(18, 141, 111, 0.1)",
                  color: "primary.main",
                  fontWeight: 500,
                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                  height: { xs: "28px", sm: "32px" },
                  "&:hover": {
                    backgroundColor: "rgba(18, 141, 111, 0.2)",
                  },
                  "&:active": {
                    backgroundColor: "rgba(18, 141, 111, 0.3)",
                  },
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
          <IconButton
            size={isMobile ? "medium" : "small"}
            onClick={handleLike}
            disabled={isLiking || !authorized}
            sx={{
              minWidth: { xs: "44px", sm: "40px" },
              minHeight: { xs: "44px", sm: "40px" },
              color: isLiked ? "error.main" : "text.secondary",
              "&:hover": {
                color: "error.main",
                backgroundColor: "rgba(211, 47, 47, 0.1)",
              },
            }}
          >
            {isLiked ? (
              <FavoriteIcon fontSize={isMobile ? "medium" : "small"} />
            ) : (
              <FavoriteBorderIcon fontSize={isMobile ? "medium" : "small"} />
            )}
          </IconButton>
          {likeCount > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                minWidth: "20px",
              }}
            >
              {likeCount}
            </Typography>
          )}
          <IconButton
            size={isMobile ? "medium" : "small"}
            onClick={() => onToggleComments(rate)}
            sx={{
              ml: { xs: 0, sm: 0 },
              minWidth: { xs: "44px", sm: "40px" },
              minHeight: { xs: "44px", sm: "40px" },
              color: expandedComments[rate.id] ? "primary.main" : "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(18, 141, 111, 0.1)",
              },
              "&:active": {
                backgroundColor: "rgba(18, 141, 111, 0.2)",
              },
            }}
          >
            <CommentIcon fontSize={isMobile ? "medium" : "small"} />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expandedComments[rate.id]}>
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          }}
        >
          {(comments[rate.id] || []).length > 0 ? (
            <Box sx={{ mb: 2 }}>
              {(comments[rate.id] || []).map((comment) => (
                <RateComment
                  key={comment.id}
                  comment={comment}
                  authorized={authorized}
                  submitting={submittingReplies}
                  newReply={newReplies}
                  onReplyChange={handleReplyChange}
                  onSubmitReply={handleSubmitReply}
                  onToggleReplies={handleToggleReplies}
                  expandedReplies={expandedReplies}
                  onDeleteComment={onDeleteComment}
                  currentUser={currentUser}
                  isSpaceOwner={isSpaceOwner}
                />
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontStyle: "italic" }}
            >
              No comments yet
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.75, sm: 1 },
              alignItems: "flex-start",
              mt: { xs: 1.5, sm: 2 },
              p: { xs: 1.5, sm: 2 },
              backgroundColor: 'background.paper',
              borderRadius: { xs: 1.5, sm: 2 },
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <TextField
              size={isMobile ? "small" : "small"}
              fullWidth
              placeholder="Write a comment..."
              value={newComments[rate.id] || ""}
              onChange={(e) => onCommentChange(rate.id, e.target.value)}
              disabled={submitting[rate.id] || !authorized}
              multiline
              rows={isMobile ? 2 : 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  '&:hover': {
                    backgroundColor: 'background.paper',
                  },
                },
              }}
            />
            <IconButton
              onClick={() => onSubmitComment(rate)}
              disabled={
                !newComments[rate.id]?.trim() || submitting[rate.id] || !authorized
              }
              color="primary"
              size={isMobile ? "medium" : "small"}
              sx={{
                mt: { xs: 0.25, sm: 0.5 },
                minWidth: { xs: "44px", sm: "40px" },
                minHeight: { xs: "44px", sm: "40px" },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)',
                },
                '&:active': {
                  backgroundColor: 'rgba(18, 141, 111, 0.2)',
                },
              }}
              title={!authorized ? "Login to comment" : "Send comment"}
            >
              <SendIcon fontSize={isMobile ? "medium" : "small"} />
            </IconButton>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default RateCard; 